import { Component, ViewChild, OnInit } from "@angular/core";
import { MatSort, MatSortable, Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { Router } from "@angular/router";
import { ERTEmergencyResponseService } from "../services/emergency-response/emergency-response.service";
import { ERTEmergencyResponse } from "../interfaces/emergencyresponse/emergencyresponse";
import { ERTMedicalRunSheetService } from "../services/emergency-response/medical-runsheet.service";
import { FormBuilder, FormGroup } from "@angular/forms";
import {
  FilterCriteria,
  FilterService,
} from "../services/common/filter.service";
import { LoggedUserService } from "../services/common/logged-user.service";
import { ILoggedUserData } from "../interfaces/common/loggedUserData";

@Component({
  selector: "ert-emergency-response-component",
  templateUrl: "./ert-emergency-response.component.html",
  styles: [
    `
      .button-container {
        display: flex;
        justify-content: space-between;
        margin-bottom: 10px;
      }
    `,
  ],
})
export class ERTEmergencyResponseComponent implements OnInit {
  title = "Emergency Response";
  totalItems: number;
  sortedBy: string;
  filterKey: string;
  loading: boolean = true;
  dataLoaded: boolean = false;
  dataSource = new MatTableDataSource<ERTEmergencyResponse>();
  pageSize = 10;
  searchForm: FormGroup;

  // Filter properties
  activeFilters: FilterCriteria[] = [];
  showFilterPanel = false;

  // Storage keys - use unique keys for this component
  readonly emergency_response_filters_storage_key =
    "ert-emergency-response-filters";
  readonly emergency_response_saved_filters_storage_key =
    "ert-emergency-response-saved-filters";

  displayedColumns: Map<string, string> = new Map<string, string>([
    ["id", "ID"],
    ["dateTimeIncidentOccured", "Incident DateTime"],
    ["description", "Description"],
    ["incidentCommander", "Commander"],
    ["isERTCallOut", "Callout"],
    ["isFire", "Fire"],
    ["isHazmat", "Hazmat"],
    ["isLeak", "Leak"],
    ["isMedical", "Medical"],
    ["isRescue", "Rescue"],
    ["isOdor", "Odor"],
    ["isOther", "Other"],
    ["otherDescription", "OtherDescription"],
    ["options", "Options"],
  ]);
  displayedColumnsArray: string[] = Array.from(this.displayedColumns.keys());

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  currentUser: ILoggedUserData;

  constructor(
    private ertEmergencyResponseService: ERTEmergencyResponseService,
    private ertMedicalRunSheetService: ERTMedicalRunSheetService,
    private formBuilder: FormBuilder,
    private router: Router,
    private filterService: FilterService,
    private loggedUserService: LoggedUserService
  ) {}

  ngOnInit(): void {
    this.initSearchForm();
    this.ertMedicalRunSheetService.ertMedicalRunSheetForm.reset();
    this.ertEmergencyResponseService.addEmergencyResponseForm.reset();

    // Get user data and load emergency responses after that
    this.loggedUserService.getLoggedUserData().subscribe((user) => {
      this.currentUser = user;
      if (user && !this.dataLoaded) {
        // Initialize filter service inside the user data callback
        this.filterService.loadActiveFiltersFromStorage(
          this.emergency_response_filters_storage_key
        );
        this.filterService.loadSavedFiltersFromStorage(
          this.emergency_response_saved_filters_storage_key
        );

        const facilityId = Number(user.facilityId) || 1;
        this.loadEmergencyResponses(facilityId);
        this.dataLoaded = true; // Mark data as loaded to prevent duplicate loads
      }
    });
  }

  loadEmergencyResponses(facilityId: number): void {
    this.loading = true;
    this.ertEmergencyResponseService
      .getAllERTEmergencyResponses(facilityId)
      .subscribe((data) => {
        const mappedData = data.map((item: ERTEmergencyResponse) => ({
          ...item,
          facility: item.incidentCommander.facility,
          facilityId: item.incidentCommander.facilityId || 1,
          incidentCommander: item.incidentCommander,
        }));
        this.dataSource = new MatTableDataSource<
          ERTEmergencyResponse,
          MatPaginator
        >(mappedData);
        this.totalItems = mappedData.length;
        setTimeout(() => {
          this.dataSource.sort = this.sort;
          this.dataSource.paginator = this.paginator;
          // Sort by dateTimeIncidentOccured in descending order (newest first)
          this.sort.sort({
            id: "dateTimeIncidentOccured",
            start: "desc",
          } as MatSortable);
          this.sort.sortChange.subscribe((sortState: Sort) => {
            this.onSortChange(sortState);
          });
          this.sortedBy = `Sorted by ${this.displayedColumns.get(
            "dateTimeIncidentOccured"
          )}`;
        }, 10);

        // Subscribe to active filters changes
        this.filterService
          .getActiveFilters(this.emergency_response_filters_storage_key)
          .subscribe((filters) => {
            this.activeFilters = filters;
            if (this.dataSource) {
              this.applyFilter(this.searchForm.get("searchTerm")?.value || "");
            }
          });
        this.loading = false;
      });
  }

  initSearchForm(): void {
    this.searchForm = this.formBuilder.group({
      searchTerm: [""],
    });
    this.searchForm.get("searchTerm")?.valueChanges.subscribe((value) => {
      this.applyFilter(value);
    });
  }

  applyFilter(filterValue: string): void {
    this.dataSource.filterPredicate = (
      data: ERTEmergencyResponse,
      filterString: string
    ) => {
      const parts = filterString.split("||");
      const searchTerm = parts[0].toLowerCase();
      let activeFilters: FilterCriteria[] = [];

      try {
        if (parts[1]) {
          activeFilters = JSON.parse(parts[1]);
        }
      } catch (error) {
        console.error("Error parsing active filters:", error);
      }

      // Basic search
      const searchMatch = searchTerm
        ? String(data.id || "")
            .toLowerCase()
            .includes(searchTerm) ||
          (data.description?.toLowerCase() || "").includes(searchTerm) ||
          (data.otherDescription?.toLowerCase() || "").includes(searchTerm) ||
          (data.incidentCommander?.fullName?.toLowerCase() || "").includes(
            searchTerm
          )
        : true; // If no search term, consider it a match

      // Advanced filters match
      let advancedFiltersMatch = true;
      if (activeFilters.length > 0) {
        advancedFiltersMatch = activeFilters.every((filter) => {
          const value = this.getPropertyValue(data, filter.column);

          if (value === null || value === undefined) {
            return false;
          }

          switch (filter.operator) {
            case "contains":
              return String(value)
                .toLowerCase()
                .includes(String(filter.value).toLowerCase());
            case "equals":
              return (
                String(value).toLowerCase() ===
                String(filter.value).toLowerCase()
              );
            case "startsWith":
              return String(value)
                .toLowerCase()
                .startsWith(String(filter.value).toLowerCase());
            case "endsWith":
              return String(value)
                .toLowerCase()
                .endsWith(String(filter.value).toLowerCase());
            case "greaterThan":
              return Number(value) > Number(filter.value);
            case "lessThan":
              return Number(value) < Number(filter.value);
            default:
              return true;
          }
        });
      }

      return searchMatch && advancedFiltersMatch;
    };

    // Combine search term and active filters
    const combinedFilter =
      filterValue.trim().toLowerCase() +
      "||" +
      JSON.stringify(this.activeFilters);
    this.dataSource.filter = combinedFilter;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }

    this.totalItems = this.dataSource.filteredData.length;
  }

  // Helper to get property value for filtering
  getPropertyValue(data: ERTEmergencyResponse, property: string): any {
    switch (property) {
      case "id":
        return data.id;
      case "dateTimeIncidentOccured":
        return data.dateTimeIncidentOccured;
      case "description":
        return data.description;
      case "incidentCommander":
        return data.incidentCommander?.fullName;
      case "isERTCallOut":
        return data.isERTCallOut ? "Yes" : "No";
      case "isFire":
        return data.isFire ? "Yes" : "No";
      case "isHazmat":
        return data.isHazmat ? "Yes" : "No";
      case "isLeak":
        return data.isLeak ? "Yes" : "No";
      case "isMedical":
        return data.isMedical ? "Yes" : "No";
      case "isOdor":
        return data.isOdor ? "Yes" : "No";
      case "isOther":
        return data.isOther ? "Yes" : "No";
      case "otherDescription":
        return data.otherDescription;
      default:
        return "";
    }
  }

  toggleFilterPanel() {
    this.showFilterPanel = !this.showFilterPanel;
  }

  onFiltersChanged(filters: FilterCriteria[]): void {
    this.applyFilter(this.searchForm.get("searchTerm")?.value || "");
  }

  clearAllFilters() {
    this.filterService.clearAllFilters(
      this.emergency_response_filters_storage_key
    );
    this.searchForm.patchValue({ searchTerm: "" });
    this.applyFilter("");
  }

  getColumnDisplayName(columnKey: string): string {
    return this.displayedColumns.get(columnKey) || columnKey;
  }

  onSortChange(sortState: Sort): void {
    if (sortState.direction.length > 0) {
      this.sortedBy = `Sorted by ${this.displayedColumns.get(
        sortState.active
      )}`;
    } else {
      this.sortedBy = "Not sorted";
    }
  }

  navigateToNewEntry(): void {
    this.router.navigate(["/ert-emergency-response/emergency-details"]);
  }

  formatDate(input: string): string {
    const date = new Date(input);

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    let hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = Number(hours) >= 12 ? "PM" : "AM";
    hours = (Number(hours) % 12).toString().padStart(2, "0");
    hours = Number(hours) ? hours : "12";
    const strTime = `${hours}:${minutes} ${ampm}`;

    return `${month}/${day}/${year} ${strTime}`;
  }

  deleteEmergencyResponse(id: number) {
    this.ertEmergencyResponseService
      .deleteERTEmergencyResponse(id, Number(this.currentUser.facilityId) || 0)
      .subscribe(() => {
        const facilityId = Number(this.currentUser.facilityId) || 1;
        this.loadEmergencyResponses(facilityId);
      });
  }

  editEmergencyResponse(id: string) {
    this.router.navigate(["/ert-emergency-response/emergency-response-view"], {
      queryParams: { emergencyResponseId: id },
    });
  }
}
